'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { trackMetaEvent } from './metaPixel'
import type { CartItem, PaymentMethodsSnapshot } from '@sifly/shared-types'
import { promoWindowOpen, qualifyingSetUnits } from '@sifly/shared-utils'

export type CartLine = CartItem & { lineId: string }

function mintLineId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `line-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function withLineIds(items: CartItem[]): CartLine[] {
  return items.map(i => (i.lineId ? (i as CartLine) : { ...i, lineId: mintLineId() }))
}

type CartContextValue = {
  tenantId: string
  /** Dealer toggle: offer free shipping when the customer pays by bank (ACH). */
  freeShippingAch: boolean
  /** The store's accessory-only shipping rate (storefront_settings.flat_accessory_shipping,
   *  dollars); undefined = SiFly's default. Preview only — the server re-reads it. */
  flatAccessoryRate: number | undefined
  /** The tenant has at least one public location that offers store pickup. */
  hasPickupLocations: boolean
  /** The dealer's storefront payment methods, redacted to what the cart may
   *  show. Resolved server-side in the layout; /api/checkout/manual re-derives
   *  the same snapshot from the tenant row and gates on ITS copy, so this is a
   *  display list, never the authority. */
  paymentMethods: PaymentMethodsSnapshot
  /** storefront_settings.checkout_mode: which page the drawer's checkout opens. */
  checkoutMode: 'hosted' | 'embedded'
  items: CartLine[]
  addItem: (item: CartItem) => void
  removeItem: (lineId: string) => void
  updateQuantity: (lineId: string, quantity: number) => void
  clearCart: () => void
  itemCount: number
  subtotal: number
  /** Labor Day promo: how many free Wing Sets this cart has earned (0 = no offer). */
  giftAllowance: number
  /** How many free Wing Sets are currently in the cart. */
  giftClaimed: number
  isDrawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
}

const CartCtx = createContext<CartContextValue | null>(null)

function storageKey(tenantId: string) {
  return `sifly-cart-${tenantId}`
}

function loadCart(tenantId: string): CartItem[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(storageKey(tenantId))
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveCart(tenantId: string, items: CartItem[]) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(storageKey(tenantId), JSON.stringify(items))
  } catch {
    // localStorage full or unavailable
  }
}

function itemKey(item: { productId: string; skuId: string | null; bundleOptionIds?: string[]; isGift?: boolean }): string {
  // The gift flag is part of the identity: a customer can own a paid Glide 900
  // AND receive a free one, and those are two lines at two prices — merging
  // them would either charge for the bonus or give away the paid one.
  const gift = item.isGift ? ':gift' : ''
  if (item.bundleOptionIds?.length) {
    return `${item.productId}:bundle:${[...item.bundleOptionIds].sort().join(',')}${gift}`
  }
  return `${item.productId}:${item.skuId ?? 'no-sku'}${gift}`
}

export function CartProvider({ tenantId, freeShippingAch = false, flatAccessoryRate, hasPickupLocations = false, paymentMethods, checkoutMode = 'hosted', children }: { tenantId: string; freeShippingAch?: boolean; flatAccessoryRate?: number; hasPickupLocations?: boolean; paymentMethods: PaymentMethodsSnapshot; checkoutMode?: 'hosted' | 'embedded'; children: React.ReactNode }) {
  const [items, setItems] = useState<CartLine[]>([])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [hydrated, setHydrated] = useState(false)

  // Hydrate from localStorage on mount.
  useEffect(() => {
    setItems(withLineIds(loadCart(tenantId)))
    setHydrated(true)
  }, [tenantId])

  // Persist on change (skip initial hydration)
  useEffect(() => {
    if (hydrated) saveCart(tenantId, items)
  }, [tenantId, items, hydrated])

  const addItem = useCallback((item: CartItem) => {
    // A gift is one unit at no charge, whatever the caller passed. The server
    // denies a gift line with quantity !== 1 outright, so normalise here rather
    // than let a stray quantity fail the whole checkout.
    const incoming: CartItem = item.isGift ? { ...item, quantity: 1, unitPrice: 0 } : item
    setItems(prev => {
      const key = itemKey(incoming)
      const existing = prev.find(i => itemKey(i) === key)
      if (existing) {
        if (incoming.isGift) return prev
        return prev.map(i => itemKey(i) === key ? { ...i, quantity: i.quantity + incoming.quantity } : i)
      }
      return [...prev, { ...incoming, lineId: incoming.lineId ?? mintLineId() }]
    })
    if (!incoming.isGift) trackMetaEvent('AddToCart', { content_ids: [incoming.productId], content_type: 'product', value: incoming.unitPrice * incoming.quantity, currency: 'USD', num_items: incoming.quantity })
    setIsDrawerOpen(true)
  }, [])

  const removeItem = useCallback((lineId: string) => {
    setItems(prev => prev.filter(i => i.lineId !== lineId))
  }, [])

  const updateQuantity = useCallback((lineId: string, quantity: number) => {
    setItems(prev => quantity <= 0
      ? prev.filter(i => i.lineId !== lineId)
      : prev.map(i => (i.lineId === lineId && !i.isGift) ? { ...i, quantity } : i))
  }, [])

  const clearCart = useCallback(() => setItems([]), [])

  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items])
  const subtotal = useMemo(() => items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0), [items])

  // Labor Day promo. One free Wing Set per eFoil set unit; the offer is gone
  // entirely once the window closes. Display-and-guardrail only — the checkout
  // route re-derives the same verdict from the same module before charging.
  const giftAllowance = useMemo(
    () => (promoWindowOpen() ? qualifyingSetUnits(items) : 0),
    [items],
  )
  const giftClaimed = useMemo(() => items.filter(i => i.isGift).length, [items])

  // Drop gifts the cart no longer earns — remove the last eFoil set and the
  // bonus goes with it, rather than riding to a checkout that would reject it.
  // Keeps the earliest-added gifts when the allowance merely shrinks.
  useEffect(() => {
    if (giftClaimed <= giftAllowance) return
    setItems(prev => {
      let kept = 0
      return prev.filter(i => !i.isGift || kept++ < giftAllowance)
    })
  }, [giftClaimed, giftAllowance])

  const openDrawer = useCallback(() => setIsDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), [])

  const value: CartContextValue = useMemo(() => ({
    tenantId,
    freeShippingAch,
    flatAccessoryRate,
    hasPickupLocations,
    paymentMethods,
    checkoutMode,
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    itemCount,
    subtotal,
    giftAllowance,
    giftClaimed,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
  }), [tenantId, freeShippingAch, flatAccessoryRate, hasPickupLocations, paymentMethods, checkoutMode, items, addItem, removeItem, updateQuantity, clearCart, itemCount, subtotal, giftAllowance, giftClaimed, isDrawerOpen, openDrawer, closeDrawer])

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartCtx)
  if (!ctx) throw new Error('useCart must be used inside CartProvider')
  return ctx
}
